import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Trash2, Edit, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminBanners() {
    const [banners, setBanners] = useState([]);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ kind: 'promo1', title: '', subtitle: '', image_url: '', link_url: '', cta_text: '', bg_color: '#dc2626', order: 1, active: true });

    const load = async () => { const { data } = await api.get('/admin/banners'); setBanners(data); };
    useEffect(() => { load(); }, []);

    const submit = async () => {
        try {
            if (editing) await api.put(`/admin/banners/${editing.id}`, form);
            else await api.post('/admin/banners', form);
            toast.success('Guardado');
            setEditing(null); setCreating(false);
            setForm({ kind: 'promo1', title: '', subtitle: '', image_url: '', link_url: '', cta_text: '', bg_color: '#dc2626', order: 1, active: true });
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    const openEdit = (b) => { setForm({ ...b }); setEditing(b); setCreating(false); };
    const openNew = () => { setForm({ kind: 'promo1', title: '', subtitle: '', image_url: '', link_url: '', cta_text: '', bg_color: '#dc2626', order: banners.length + 1, active: true }); setEditing(null); setCreating(true); };
    const toggleActive = async (b) => { await api.put(`/admin/banners/${b.id}`, { active: !b.active }); load(); };
    const del = async (id) => { if (!window.confirm('¿Eliminar banner?')) return; await api.delete(`/admin/banners/${id}`); toast.success('Eliminado'); load(); };

    return (
        <div className="space-y-4">
            <div className="flex items-baseline justify-between">
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight">Banners promocionales</h1>
                    <p className="text-sm text-muted-foreground">Configura las pasarelas <b>promo1</b> (después de anuncios) y <b>promo2</b> (después de vendedores) del home.</p>
                </div>
                <Button onClick={openNew} className="bg-red-600 hover:bg-red-700" data-testid="banner-new-btn"><Plus className="h-4 w-4 mr-1" />Nuevo banner</Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="admin-banners-grid">
                {banners.map(b => (
                    <div key={b.id} className="rounded-2xl border bg-card overflow-hidden">
                        <div className="relative h-32">
                            <img src={b.image_url} alt="" className="absolute inset-0 h-full w-full object-cover" />
                            <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${b.bg_color || '#dc2626'}cc, transparent 60%)` }} />
                            <div className="relative h-full p-3 flex flex-col justify-between text-white">
                                <div className="flex items-center justify-between">
                                    <span className="text-[10px] uppercase bg-white/20 backdrop-blur px-2 py-0.5 rounded-full">{b.kind}</span>
                                    <span className="text-[10px] bg-black/50 px-2 py-0.5 rounded-full">Orden {b.order}</span>
                                </div>
                                <div>
                                    <div className="font-bold text-sm drop-shadow">{b.title}</div>
                                    {b.subtitle && <div className="text-xs text-white/85 line-clamp-1">{b.subtitle}</div>}
                                </div>
                            </div>
                        </div>
                        <div className="p-3 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Switch checked={b.active} onCheckedChange={() => toggleActive(b)} />
                                <span className="text-xs text-muted-foreground">{b.active ? 'Activo' : 'Inactivo'}</span>
                            </div>
                            <div className="flex gap-1">
                                {b.link_url && <a href={b.link_url} target="_blank" rel="noreferrer" className="h-8 w-8 flex items-center justify-center rounded-md border hover:bg-secondary"><ExternalLink className="h-3.5 w-3.5" /></a>}
                                <Button size="sm" variant="outline" onClick={() => openEdit(b)}><Edit className="h-3.5 w-3.5" /></Button>
                                <Button size="sm" variant="outline" onClick={() => del(b.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <Dialog open={creating || !!editing} onOpenChange={(o) => { if (!o) { setCreating(false); setEditing(null); } }}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>{editing ? 'Editar banner' : 'Nuevo banner'}</DialogTitle></DialogHeader>
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label>Tipo</Label>
                            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                                <SelectTrigger data-testid="banner-kind"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="promo1">Promo 1 (después de anuncios)</SelectItem>
                                    <SelectItem value="promo2">Promo 2 (después de usuarios)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div><Label>Orden</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} /></div>
                        <div className="col-span-2"><Label>Título</Label><Input value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="banner-title" /></div>
                        <div className="col-span-2"><Label>Subtítulo</Label><Input value={form.subtitle || ''} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} /></div>
                        <div className="col-span-2"><Label>URL de imagen (Unsplash u otra)</Label><Input value={form.image_url || ''} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://images.unsplash.com/..." data-testid="banner-image-url" /></div>
                        <div><Label>Link destino</Label><Input value={form.link_url || ''} onChange={(e) => setForm({ ...form, link_url: e.target.value })} placeholder="/categories/electronica" /></div>
                        <div><Label>Texto CTA</Label><Input value={form.cta_text || ''} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} placeholder="Explorar" /></div>
                        <div><Label>Color de fondo</Label><Input type="color" value={form.bg_color || '#dc2626'} onChange={(e) => setForm({ ...form, bg_color: e.target.value })} /></div>
                        <div className="flex items-center gap-2 mt-6">
                            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
                            <Label>Activo</Label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setCreating(false); setEditing(null); }}>Cancelar</Button>
                        <Button onClick={submit} className="bg-red-600 hover:bg-red-700" data-testid="banner-submit">Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
